/**
 * BACKOFFICE LOGISTIQUE — Conteneurs / expédition / dégroupage (#662).
 * Méthodes extraites telles quelles du god-service `backoffice.service.ts`
 * (workstream C) : CRUD conteneurs, assignation colis, workflow expédition
 * (filmage, méthode, contacts, dispatch), réception Congo & dégroupage.
 */

import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, updateDoc, getDoc, getDocFromServer, addDoc, limit } from '@angular/fire/firestore';
import { arrayUnion, arrayRemove, deleteField, increment, writeBatch } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  ContainerFlight,
  ShipmentListItem,
  ReceptionSearchResult,
  ShippingContainer,
  ShippingMethodSubType,
  WEIGHT_PRESETS
} from '../models/backoffice.models';
import { mapToContainer, mapToListItem } from './backoffice-mapper';
import { BackofficeActivityService } from './backoffice-activity.service';
import { ReceptionService } from './reception.service';
import { SHIPMENT_TRANSITIONS } from '@befret/core/shipment';
import { ProductionTranslationService } from '../../core/services/production-translation.service';

/**
 * #982 — garde « destination » du groupage, extraite pour être appliquée AVANT le
 * clic aussi bien qu'au moment de l'écriture. `assignShipmentToContainer` la
 * contrôlait, mais la liste des colis proposés ne la filtrait pas : le staff
 * scannait un colis Lubumbashi dans un conteneur Kinshasa, cliquait, et le refus
 * n'atteignait jamais l'écran. Une seule implémentation, deux appelants.
 *
 * Sémantique conservée telle quelle : comparaison par **sous-chaîne** dans les
 * deux sens (un colis « Kinshasa » entre donc dans n'importe quel conteneur
 * « Kinshasa-<commune> »), et **information manquante ⇒ pas de blocage**.
 */
export function isDestinationCompatible(containerCity: string, shipmentCity: string): boolean {
  const container = (containerCity || '').toLowerCase();
  const shipment = (shipmentCity || '').toLowerCase();
  if (!container || !shipment) return true;
  return shipment.includes(container) || container.includes(shipment);
}

/** Motif affiché au staff (et levé par le service) quand les destinations divergent. */
export function destinationMismatchMessage(containerCity: string, shipmentCity: string): string {
  const container = (containerCity || '').toLowerCase();
  const shipment = (shipmentCity || '').toLowerCase();
  return `Destination incompatible : le colis est pour "${shipment}" mais le conteneur est pour "${container}".`;
}

/** #2010 — statuts d'un colis encore EN ROUTE vers le Congo (avant `local_sorting`). */
const ARRIVAL_PENDING_STATUSES: ReadonlySet<string | null> = new Set(['befret_transit', 'customs_clearance']);

/**
 * #2022 — statuts depuis lesquels un colis peut partir en `befret_transit` :
 * la règle de la matrice partagée (`@befret/core/shipment`), celle-là même que
 * le serveur applique. L'écran ne relance que ces colis-là.
 */
/**
 * Un colis déjà parti (en transit ou plus loin) : rien à relancer. Copie
 * verrouillée par un test de parité avec `AT_OR_BEYOND_TRANSIT` de la CF
 * `dispatchContainer`, qui reste seule juge avant de marquer le conteneur.
 */
export const AT_OR_BEYOND_TRANSIT: ReadonlySet<string> = new Set([
  'befret_transit', 'customs_clearance', 'local_sorting', 'delivery_sorted',
  'out_for_delivery', 'awaiting_pickup', 'delivery_attempted',
  'delivery_rescheduled', 'delivered',
]);

const DISPATCHABLE_STATUSES: ReadonlySet<string> = new Set(SHIPMENT_TRANSITIONS.befret_transit?.from ?? []);

@Injectable({ providedIn: 'root' })
export class ContainerService {

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private activity: BackofficeActivityService,
    private receptionService: ReceptionService,
    private i18n: ProductionTranslationService,
  ) {}

  // ── Conteneurs ──

  async getContainers(status?: string, limitCount = 100): Promise<ShippingContainer[]> {
    const containersRef = collection(this.firestore, 'containers');
    let q;
    if (status) {
      q = query(containersRef, where('status', '==', status), limit(limitCount));
    } else {
      q = query(containersRef, limit(limitCount));
    }
    const snap = await getDocs(q);
    return snap.docs.map(d => mapToContainer(d)).sort((a, b) =>
      b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  /** #1992 — relecture pour « Vérifier » : toujours au serveur, jamais au cache local. */
  async getContainerFromServer(containerId: string): Promise<ShippingContainer | null> {
    const snap = await getDocFromServer(doc(this.firestore, 'containers', containerId));
    if (!snap.exists()) return null;
    return mapToContainer(snap);
  }

  async getContainer(containerId: string): Promise<ShippingContainer | null> {
    const docRef = doc(this.firestore, 'containers', containerId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return mapToContainer(snap);
  }

  async createContainer(data: {
    transportMode: 'air' | 'maritime';
    maxWeightType?: 'air_23' | 'maritime_32' | 'custom';
    customMaxWeight?: number;
    destinationCity: string;
    contactChain?: any[];
    notes?: string;
    createdBy?: string;
  }): Promise<string> {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');

    // Generate unique container number with collision check
    let containerNumber = '';
    const containersRef = collection(this.firestore, 'containers');
    for (let attempt = 0; attempt < 5; attempt++) {
      const random = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
      const candidate = `CT-${dateStr}-${random}`;
      const dupCheck = query(containersRef, where('containerNumber', '==', candidate), limit(1));
      const dupSnap = await getDocs(dupCheck);
      if (dupSnap.empty) {
        containerNumber = candidate;
        break;
      }
    }
    if (!containerNumber) {
      throw new Error('Impossible de générer un numéro de groupage unique. Réessayez.');
    }

    // Determine max weight from preset or custom value
    const weightType = data.maxWeightType || (data.transportMode === 'air' ? 'air_23' : 'maritime_32');
    let maxWeightKg: number;
    if (weightType === 'custom' && data.customMaxWeight) {
      maxWeightKg = data.customMaxWeight;
    } else {
      maxWeightKg = WEIGHT_PRESETS[weightType]?.maxKg || (data.transportMode === 'air' ? 23 : 32);
    }

    const docRef = await addDoc(containersRef, {
      containerNumber,
      transportMode: data.transportMode,
      maxWeightKg,
      maxWeightType: weightType,
      currentWeightKg: 0,
      status: 'open',
      expeditionStep: 'groupage',
      shipmentIds: [],
      shipmentCount: 0,
      destinationCity: data.destinationCity,
      departure: { departureWeight: 0 },
      contactChain: data.contactChain || [],
      createdAt: now,
      createdBy: data.createdBy || 'staff',
      updatedAt: now,
      notes: data.notes || '',
    });
    return docRef.id;
  }

  async assignShipmentToContainer(shipmentId: string, containerId: string, containerNumber: string, shipmentWeight: number): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const now = new Date();
    const location = await this.activity.captureLocation();

    // GUARD: validate destination match
    const containerSnap = await getDoc(containerRef);
    const shipmentSnap = await getDoc(shipmentRef);
    if (!containerSnap.exists() || !shipmentSnap.exists()) throw new Error('Conteneur ou colis introuvable');

    const containerCity = (containerSnap.data()?.['destinationCity'] || '').toLowerCase();
    const shipmentCity = (shipmentSnap.data()?.['destination']?.['city'] || shipmentSnap.data()?.['customerInfo']?.['receiver']?.['address']?.['city'] || '').toLowerCase();
    if (!isDestinationCompatible(containerCity, shipmentCity)) {
      throw new Error(destinationMismatchMessage(containerCity, shipmentCity));
    }

    // #740 — gardes centralisées ici plutôt que dans les templates : le chemin
    // manuel (bouton désactivé) et le chemin auto ?shipmentId= partageaient ce
    // service sans partager les vérifications. Tout appelant hérite du refus.
    const containerData = containerSnap.data() || {};
    if (containerData['status'] !== 'open') {
      throw new Error(`Ce groupage n'est pas ouvert (statut « ${containerData['status'] || 'inconnu'} »).`);
    }
    if ((containerData['shipmentIds'] || []).includes(shipmentId)) {
      throw new Error('Ce colis est déjà dans ce groupage.');
    }
    const shipmentStatus = shipmentSnap.data()?.['status']?.['current'];
    if (shipmentStatus !== 'destination_sorted') {
      throw new Error(`Colis non préparé (statut « ${shipmentStatus || 'inconnu'} ») — seuls les colis triés-destination peuvent rejoindre un groupage.`);
    }

    // Use batch for atomicity (BUG 35 fix)
    const batch = writeBatch(this.firestore);

    batch.update(containerRef, {
      shipmentIds: arrayUnion(shipmentId),
      shipmentCount: increment(1),
      currentWeightKg: increment(shipmentWeight),
      updatedAt: now,
    });

    batch.update(shipmentRef, {
      'expedition.containerId': containerId,
      'expedition.containerNumber': containerNumber,
      'expedition.assignedAt': now,
      'timestamps.updatedAt': now,
      ...this.activity.buildActivityEvent('assigned_to_container', `Assigné au groupage ${containerNumber}`, 'expedition', 'backoffice_expedition', { containerId, containerNumber, ...(location && { location }) })
    });

    await batch.commit();
  }

  async removeShipmentFromContainer(shipmentId: string, containerId: string, shipmentWeight: number): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const now = new Date();

    // Guard: verify shipment is actually in this container before removing
    const containerSnap = await getDoc(containerRef);
    if (!containerSnap.exists()) return;
    const shipmentIds: string[] = containerSnap.data()?.['shipmentIds'] || [];
    if (!shipmentIds.includes(shipmentId)) return; // Already removed — prevent double-decrement

    // #414 — batch atomique (l'assignation utilisait déjà un batch, pas le
    // retrait : un échec partiel laissait compteur et colis désalignés).
    const batch = writeBatch(this.firestore);
    batch.update(containerRef, {
      shipmentIds: arrayRemove(shipmentId),
      shipmentCount: increment(-1),
      currentWeightKg: increment(-shipmentWeight),
      updatedAt: now,
    });
    batch.update(shipmentRef, {
      'expedition.containerId': null,
      'expedition.containerNumber': null,
      'expedition.assignedAt': null,
      'timestamps.updatedAt': now,
    });
    await batch.commit();
  }

  async closeContainer(containerId: string, sealNumber: string, departureWeight: number): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    // Validate container has shipments before closing
    const container = await this.getContainer(containerId);
    if (container && container.shipmentCount === 0) {
      throw new Error('Impossible de fermer un groupage vide. Ajoutez des colis d\'abord.');
    }
    // #414 — précondition de transition : seul un conteneur OUVERT se ferme
    // (un conteneur arrived/dispatched pouvait repasser « closed »).
    if (container && container.status !== 'open') {
      throw new Error(`Impossible de fermer un conteneur en statut « ${container.status} ». Seul un conteneur ouvert peut être fermé.`);
    }
    const now = new Date();
    await updateDoc(containerRef, {
      status: 'closed',
      expeditionStep: 'closed',
      'departure.closedAt': now,
      'departure.closedBy': 'warehouse_staff',
      'departure.departureWeight': departureWeight,
      'departure.sealNumber': sealNumber,
      updatedAt: now,
    });
  }

  async dispatchContainer(containerId: string, estimatedArrival: Date): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();

    // GARDES UX (#757/#738) : messages immédiats sans aller-retour CF. La
    // VÉRITÉ est serveur (#770) — la CF `dispatchContainer` re-valide tout
    // (status closed, méthode, contacts, date) et les rules bloquent tout
    // write client de `status:'dispatched'`.
    const containerSnap = await getDoc(containerRef);
    if (!containerSnap.exists()) throw new Error('Conteneur introuvable');
    const containerStatus = containerSnap.data()?.['status'];
    if (containerStatus !== 'closed') {
      throw new Error(`Impossible d'expédier un conteneur en statut "${containerStatus}". Le conteneur doit être fermé.`);
    }
    if (!estimatedArrival || estimatedArrival <= now) {
      throw new Error('La date d\'arrivée estimée doit être dans le futur.');
    }
    const containerData = containerSnap.data() || {};
    if (!containerData['shippingMethodDetails']?.['method']) {
      throw new Error('Méthode d\'expédition manquante — complétez l\'étape « Méthode » dans /backoffice/expedition avant d\'expédier.');
    }
    const expContacts = containerData['expeditionContacts'];
    const hasRequiredContacts = !!expContacts?.['closedAt']
      && !!expContacts?.['responsableGroupageA']?.['memberId']
      && !!expContacts?.['contactExpedition']?.['memberId']
      && !!expContacts?.['contactReception']?.['memberId'];
    if (!hasRequiredContacts) {
      throw new Error('Contacts d\'expédition incomplets ou étape contacts non clôturée — complétez l\'étape « Contacts » dans /backoffice/expedition avant d\'expédier.');
    }

    const location = await this.activity.captureLocation();
    await this.completeDispatch(containerId, containerData, estimatedArrival, location);
  }

  /**
   * #2022 — les colis passent par transitionShipmentStatus, la SEULE
   * implémentation des transitions (frise du client, achats VM, gardes de
   * paiement) ; la CF `dispatchContainer` ne fait plus que vérifier qu'ils
   * sont tous partis, puis marquer le conteneur. Rejouable : un colis déjà
   * en transit n'est jamais relancé. Jette si un colis n'est pas parti.
   */
  async completeDispatch(
    containerId: string,
    containerData: Record<string, any>,
    estimatedArrival: Date,
    location: unknown,
  ): Promise<void> {
    const shipmentIds: string[] = Array.isArray(containerData['shipmentIds']) ? containerData['shipmentIds'] : [];
    // Préconditions revérifiées par le SERVEUR avant le premier départ : un
    // refus jette ici, aucun colis n'a bougé.
    await this.callable('dispatchContainer')({ containerId, estimatedArrival: estimatedArrival.toISOString(), checkOnly: true });
    const notMoved = await this.transitionContainerShipmentsToTransit(
      { id: containerId, containerNumber: containerData['containerNumber'] ?? '', shipmentIds }, location);

    const out = notMoved.length === 0
      ? (await this.callable('dispatchContainer')({ containerId, estimatedArrival: estimatedArrival.toISOString() })).data
      : null;
    if (!out?.dispatched) {
      const pending: string[] = notMoved.length > 0 ? notMoved.map((n) => n.shipmentId) : (out?.pending ?? []);
      const reason = notMoved.find((n) => n.reason)?.reason;
      throw new Error(this.i18n.instant('BACKOFFICE.ACTION_ERROR.DISPATCH_INCOMPLETE', {
        count: pending.length,
        total: shipmentIds.length,
      }) + (reason ? ` ${reason}` : ''));
    }
  }

  /**
   * #2022 — fait passer en `befret_transit`, par `transitionShipmentStatus`,
   * les SEULS colis lus dans un statut d'où la matrice autorise ce départ.
   * Rend les colis qui ne sont pas partis : transition refusée (avec le motif
   * du serveur, ex. supplément impayé), illisibles (jamais relancés à
   * l'aveugle), ou bloqués dans un statut qui ne part pas.
   */
  async transitionContainerShipmentsToTransit(
    container: { id: string; containerNumber: string; shipmentIds: string[] },
    location: unknown,
  ): Promise<Array<{ shipmentId: string; reason?: string }>> {
    const reads = await Promise.allSettled(container.shipmentIds.map((sid) => this.readShipmentStatus(sid)));
    const candidates: string[] = [];
    const notMoved: Array<{ shipmentId: string; reason?: string }> = [];
    container.shipmentIds.forEach((sid, i) => {
      const r = reads[i];
      if (r.status === 'rejected') notMoved.push({ shipmentId: sid });
      else if (r.value && DISPATCHABLE_STATUSES.has(r.value)) candidates.push(sid);
      else if (!r.value || !AT_OR_BEYOND_TRANSIT.has(r.value)) notMoved.push({ shipmentId: sid });
    });
    const fn = this.callable('transitionShipmentStatus');
    const results = await Promise.allSettled(candidates.map((sid) =>
      fn({
        shipmentId: sid,
        targetStatus: 'befret_transit',
        details: { containerId: container.id, containerNumber: container.containerNumber },
        ...(location ? { location } : {}),
      })
    ));
    candidates.forEach((sid, i) => {
      const r = results[i];
      if (r.status === 'rejected') {
        const msg = typeof (r.reason as any)?.message === 'string' ? (r.reason as any).message.trim() : '';
        notMoved.push({ shipmentId: sid, ...(msg && msg.toLowerCase() !== 'internal' ? { reason: msg } : {}) });
      }
    });
    return notMoved;
  }

  async getShipmentsInContainer(containerId: string): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const q = query(shipmentsRef, where('expedition.containerId', '==', containerId));
    const snap = await getDocs(q);
    return snap.docs.map(d => mapToListItem(d));
  }

  /**
   * Colis en entrepôt, triés destination, pas encore dans un conteneur.
   *
   * Suivi #662 : ce corps était **byte-identique** à
   * `ReceptionService.getShipmentsForClassification` (même `where`, même filtre
   * client, même mapping, même tri) — seul le défaut de `limitCount` différait
   * (200 ici, 500 côté classement). On délègue plutôt que de dupliquer ; le
   * défaut 200 est **conservé** pour ne rien changer au comportement de
   * `expedition-groupage`. Les deux écrans veulent le même ensemble : si le
   * prédicat doit diverger un jour, ré-écrire une query propre ici.
   */
  getShipmentsReadyForExpedition(limitCount = 200): Promise<ShipmentListItem[]> {
    return this.receptionService.getShipmentsForClassification(limitCount);
  }

  /** #2161 — même liste, avec l'indication que le plafond de lecture a été atteint. */
  getShipmentsReadyForExpeditionPage(limitCount = 200): Promise<{ items: ShipmentListItem[]; truncated: boolean }> {
    return this.receptionService.getShipmentsForClassificationPage(limitCount);
  }

  async updateContainerContactChain(containerId: string, contactChain: any[]): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    await updateDoc(containerRef, { contactChain, updatedAt: new Date() });
  }

  // ── Réception Congo ──

  async searchContainerByNumber(containerNumber: string): Promise<ShippingContainer | null> {
    const trimmed = containerNumber.trim().toUpperCase();
    if (!trimmed) return null;

    const containersRef = collection(this.firestore, 'containers');
    const q = query(containersRef, where('containerNumber', '==', trimmed), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return mapToContainer(snap.docs[0]);
  }

  /**
   * Tab 1: Confirm container reception (status change only, NO shipment updates)
   */
  async confirmContainerReception(
    containerId: string,
    condition: 'good' | 'damaged' | 'partial',
    receivedBy: string
  ): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const container = await this.getContainer(containerId);
    if (!container) throw new Error('Container not found');
    // #414 — précondition : seul un conteneur expédié peut « arriver » (un
    // conteneur open/closed ne doit pas sauter les étapes).
    if (!['dispatched', 'in_transit'].includes(container.status)) {
      throw new Error(`Impossible de confirmer l'arrivée d'un conteneur en statut « ${container.status} ».`);
    }

    const now = new Date();

    await updateDoc(containerRef, {
      status: 'arrived',
      'arrival.arrivedAt': now,
      'arrival.condition': condition,
      'arrival.receivedBy': receivedBy,
      updatedAt: now,
    });
  }

  /**
   * Tab 2: Weigh container + photo → then update all shipments to congo_distribution
   * Returns weight delta and whether alert was triggered (|delta| > 0.05 kg = 50g)
   */
  /**
   * #2010 — pesée d'arrivée au Congo, REJOUABLE (décision du 16/10, voie C).
   * 1. `recordContainerWeighing` (serveur) enregistre la pesée — ou rend celle
   *    déjà posée, sans la réécrire ;
   * 2. les colis encore en route passent en `local_sorting` par
   *    `transitionShipmentStatus`, la seule implémentation des transitions
   *    (frise client, achats VM, gardes de paiement).
   * Rend `pending` : les colis qui n'ont PAS changé d'étape. Tant qu'il en
   * reste, la pesée n'est pas terminée ; une relance ne vise qu'eux.
   */
  async weighContainerArrival(
    containerId: string,
    arrivalWeight: number,
    groupagePhotoUrl: string
  ): Promise<{
    weightDelta: number; alertTriggered: boolean; alreadyWeighed: boolean; pending: string[];
    /** La pesée ENREGISTRÉE (au rejeu : celle de la première pesée, pas la saisie). */
    arrivalWeight: number; groupagePhotoUrl: string;
  }> {
    const { data } = await this.callable('recordContainerWeighing')({ containerId, arrivalWeight, groupagePhotoUrl });
    const container = await this.getContainer(containerId);
    const pending = await this.completeArrivalTransitions({
      id: containerId,
      containerNumber: container?.containerNumber ?? '',
      shipmentIds: Array.isArray(data?.shipmentIds) ? data.shipmentIds : [],
      // La photo ENREGISTRÉE, pas celle envoyée : au rejeu, c'est celle de la première pesée.
      arrival: { arrivalWeight: data?.arrivalWeight, weightDelta: data?.weightDelta, groupagePhotoUrl: data?.groupagePhotoUrl ?? groupagePhotoUrl },
    });
    return {
      weightDelta: data?.weightDelta ?? 0,
      alertTriggered: data?.alertTriggered === true,
      alreadyWeighed: data?.alreadyWeighed === true,
      pending,
      arrivalWeight: typeof data?.arrivalWeight === 'number' ? data.arrivalWeight : arrivalWeight,
      groupagePhotoUrl: data?.groupagePhotoUrl ?? groupagePhotoUrl,
    };
  }

  /**
   * #2010 — colis du conteneur encore EN ROUTE (`befret_transit` ou
   * `customs_clearance`), lus au SERVEUR. Un colis illisible (coupure) compte
   * comme en route : on ne déclare jamais « terminé » sans l'avoir vu.
   */
  async pendingArrivalShipments(shipmentIds: string[]): Promise<string[]> {
    const { inTransit, unreadable } = await this.classifyArrivalShipments(shipmentIds);
    return shipmentIds.filter((sid) => inTransit.includes(sid) || unreadable.includes(sid));
  }

  /** Colis LUS en route, et colis illisibles (jamais relancés à l'aveugle). */
  private async classifyArrivalShipments(shipmentIds: string[]): Promise<{ inTransit: string[]; unreadable: string[] }> {
    const reads = await Promise.allSettled(shipmentIds.map((sid) => this.readShipmentStatus(sid)));
    return {
      inTransit: shipmentIds.filter((_, i) => reads[i].status === 'fulfilled'
        && ARRIVAL_PENDING_STATUSES.has((reads[i] as PromiseFulfilledResult<string | null>).value)),
      unreadable: shipmentIds.filter((_, i) => reads[i].status === 'rejected'),
    };
  }

  /**
   * #2010 — fait passer en `local_sorting` les SEULS colis encore en route.
   * Un colis déjà arrivé (ou plus loin) n'est jamais relancé, un colis
   * illisible non plus : un statut inconnu peut être un colis déjà avancé, que
   * le serveur refuserait sans fin. Rend ceux qui restent en route (échecs et
   * illisibles) après cette passe.
   */
  async completeArrivalTransitions(container: {
    id: string;
    containerNumber: string;
    shipmentIds: string[];
    arrival?: { arrivalWeight?: number; weightDelta?: number; groupagePhotoUrl?: string } | null;
  }): Promise<string[]> {
    const { inTransit: pending, unreadable } = await this.classifyArrivalShipments(container.shipmentIds);
    if (pending.length === 0) return unreadable;
    const location = await this.activity.captureLocation();
    const fn = this.callable('transitionShipmentStatus');
    const results = await Promise.allSettled(pending.map((sid) =>
      fn({
        shipmentId: sid,
        targetStatus: 'local_sorting',
        details: {
          containerId: container.id,
          containerNumber: container.containerNumber,
          ...(typeof container.arrival?.arrivalWeight === 'number' && { arrivalWeight: container.arrival.arrivalWeight }),
          ...(typeof container.arrival?.weightDelta === 'number' && { weightDelta: container.arrival.weightDelta }),
          ...(container.arrival?.groupagePhotoUrl && { photoUrl: container.arrival.groupagePhotoUrl }),
        },
        ...(location && { location }),
      })
    ));
    return [...pending.filter((_, i) => results[i].status === 'rejected'), ...unreadable];
  }

  /** Statut courant d'un colis, lu au SERVEUR (jamais au cache local). Point d'entrée des tests. */
  protected async readShipmentStatus(shipmentId: string): Promise<string | null> {
    const snap = await getDocFromServer(doc(this.firestore, 'shipments', shipmentId));
    const data: any = snap.exists() ? snap.data() : null;
    return data?.status?.current ?? data?.currentStatus ?? null;
  }

  /** Appel d'une callable. Point d'entrée des tests. */
  protected callable(name: string): (payload: unknown) => Promise<{ data: any }> {
    return httpsCallable<any, any>(this.functions, name);
  }

  // ── Dégroupage ──

  async scanParcelInContainer(
    containerId: string,
    trackingCode: string,
    arrivalWeight: number
  ): Promise<{ found: boolean; weightDelta: number; shipmentInfo?: ReceptionSearchResult }> {
    const shipment = await this.receptionService.searchByDpdCode(trackingCode);
    if (!shipment) return { found: false, weightDelta: 0 };

    const container = await this.getContainer(containerId);
    if (!container || !container.shipmentIds.includes(shipment.id)) {
      return { found: false, weightDelta: 0 };
    }

    const now = new Date();
    // Use befretWeight (warehouse weighed) for delta, fallback to declared weight
    const shipmentDoc = await getDoc(doc(this.firestore, 'shipments', shipment.id));
    const shipData = shipmentDoc.data();
    const departureWeight = shipData?.['warehouse']?.['befretWeight'] || shipment.weight;
    const weightDelta = arrivalWeight - departureWeight;
    const location = await this.activity.captureLocation();

    const shipmentRef = doc(this.firestore, 'shipments', shipment.id);

    await updateDoc(shipmentRef, {
      'congoArrival.arrivalWeight': arrivalWeight,
      'congoArrival.weightDeltaFromDeparture': weightDelta,
      'congoArrival.scannedAt': now,
      'congoArrival.scannedBy': 'congo_staff',
      'timestamps.updatedAt': now,
      ...this.activity.buildActivityEvent('parcel_scanned_congo', `Scanné au Congo — ${arrivalWeight}kg (delta: ${weightDelta > 0 ? '+' : ''}${weightDelta.toFixed(1)}kg)`, 'delivery', 'backoffice_degroupage', { containerId, arrivalWeight, weightDelta, ...(location && { location }) })
    });

    // (Le miroir `parcelLocationHistory` de la carte client est dérivé
    // server-side par le trigger syncStatusToLocationMirror depuis
    // shipments.statusHistory — T14/C3 Phase 2. L'appel client d'origine,
    // recordLocationForMap(), a été retiré à ce moment-là ; seul son `if` vide
    // était resté.)

    // Add to container scanned list
    const containerRef = doc(this.firestore, 'containers', containerId);
    await updateDoc(containerRef, {
      'unpacking.scannedShipmentIds': arrayUnion(shipment.id),
      'unpacking.startedAt': container.unpacking?.startedAt || now,
      updatedAt: now,
    });

    return { found: true, weightDelta, shipmentInfo: shipment };
  }

  async completeUnpacking(containerId: string): Promise<void> {
    const container = await this.getContainer(containerId);
    if (!container) throw new Error('Container not found');

    const scanned = container.unpacking?.scannedShipmentIds || [];
    const missing = container.shipmentIds.filter(id => !scanned.includes(id));

    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    await updateDoc(containerRef, {
      status: 'unpacked',
      'unpacking.completedAt': now,
      'unpacking.missingShipmentIds': missing,
      updatedAt: now,
    });
  }

  async validateScannedParcel(
    shipmentId: string,
    status: 'validated' | 'blocked' | 'pending',
    photoUrl?: string
  ): Promise<void> {
    // #739 — défense en profondeur : un blocked (écart significatif) exige
    // une photo-preuve, quel que soit l'appelant.
    if (status === 'blocked' && !photoUrl) {
      throw new Error('Photo obligatoire pour enregistrer un colis bloqué (écart de poids significatif).');
    }
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    const now = new Date();
    const location = await this.activity.captureLocation();

    const updateData: Record<string, any> = {
      'congoArrival.parcelStatus': status,
      'timestamps.updatedAt': now,
      ...this.activity.buildActivityEvent(
        `parcel_${status}`,
        `Colis ${status === 'validated' ? 'validé' : status === 'blocked' ? 'bloqué' : 'en attente'}`,
        'delivery',
        'backoffice_degroupage',
        { status, ...(photoUrl && { photoUrl }), ...(location && { location }) }
      )
    };

    if (photoUrl) {
      updateData['congoArrival.parcelPhotoUrl'] = photoUrl;
    }

    await updateDoc(shipmentRef, updateData);
  }

  // ── Expedition workflow methods ──

  async confirmFilmage(containerId: string, confirmedBy: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    await updateDoc(containerRef, {
      'filmage.confirmed': true,
      'filmage.confirmedAt': now,
      'filmage.confirmedBy': confirmedBy,
      updatedAt: now,
    });
  }

  async saveGroupagePhoto(containerId: string, photoUrl: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    await updateDoc(containerRef, {
      groupagePhotoUrl: photoUrl,
      updatedAt: new Date(),
    });
  }

  async saveContainerLabel(containerId: string, labelUrl: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    await updateDoc(containerRef, {
      containerLabelUrl: labelUrl,
      updatedAt: new Date(),
    });
  }

  async setShippingMethod(
    containerId: string,
    method: 'air' | 'maritime',
    subType?: ShippingMethodSubType,
    partnerId?: string,
    partnerName?: string,
    flight?: ContainerFlight,
  ): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    const shippingMethodDetails: any = { method };
    if (subType) shippingMethodDetails.subType = subType;
    if (partnerId) shippingMethodDetails.partnerId = partnerId;
    if (partnerName) shippingMethodDetails.partnerName = partnerName;

    await updateDoc(containerRef, {
      shippingMethodDetails,
      // #2256 — changer de partenaire ne laisse pas l'ancien vol sur le groupage.
      flight: flight ?? deleteField(),
      expeditionStep: 'shipping_method',
      updatedAt: now,
    });
  }

  async setExpeditionContact(
    containerId: string,
    role: 'responsableGroupageA' | 'contactExpedition' | 'contactReception' | 'responsableGroupageB',
    memberId: string,
    memberName: string
  ): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    await updateDoc(containerRef, {
      [`expeditionContacts.${role}.memberId`]: memberId,
      [`expeditionContacts.${role}.memberName`]: memberName,
      [`expeditionContacts.${role}.confirmedAt`]: now,
      updatedAt: now,
    });
  }

  async openContactStep(containerId: string, openedBy: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    await updateDoc(containerRef, {
      'expeditionContacts.openedBy': openedBy,
      'expeditionContacts.openedAt': now,
      expeditionStep: 'contact',
      updatedAt: now,
    });
  }

  async closeContactStep(containerId: string, closedBy: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    const now = new Date();
    await updateDoc(containerRef, {
      'expeditionContacts.closedBy': closedBy,
      'expeditionContacts.closedAt': now,
      expeditionStep: 'ready_to_dispatch',
      updatedAt: now,
    });
  }

  async updateExpeditionStep(containerId: string, step: string): Promise<void> {
    const containerRef = doc(this.firestore, 'containers', containerId);
    await updateDoc(containerRef, {
      expeditionStep: step,
      updatedAt: new Date(),
    });
  }

  async dispatchContainerWithNotifications(
    containerId: string,
    estimatedArrival: Date
  ): Promise<{ success: boolean; notificationResults?: any }> {
    // First dispatch the container (existing logic)
    await this.dispatchContainer(containerId, estimatedArrival);

    // Then send notifications via cloud function
    const container = await this.getContainer(containerId);
    if (!container) return { success: true };

    try {
      const fn = httpsCallable(this.functions, 'sendExpeditionNotifications');
      const result = await fn({
        containerId,
        containerNumber: container.containerNumber,
        estimatedArrival: estimatedArrival.toISOString(),
        destinationCity: container.destinationCity,
        shipmentIds: container.shipmentIds,
      });
      return { success: true, notificationResults: result.data };
    } catch (err: any) {
      console.warn('Expedition notifications failed (container still dispatched):', err);
      return {
        success: true,
        notificationResults: {
          error: 'Les notifications SMS/Email n\'ont pas pu être envoyées. Le conteneur a bien été expédié.',
          details: err?.message || 'Erreur réseau'
        }
      };
    }
  }
}
